import { useReactiveVar } from '@apollo/client';
import { makeImmerVar } from 'immerable/makeImmerVar';
import { useAsyncEffect } from 'react-utils/hooks/useAsyncEffect';
import { waitForConnectedMagpie } from '../utils/magpie';
import { MagpieConnection } from 'magpie-lib/connection';
import { getDeferred } from '../utils/common';
import { StateCommands, StateMessages, stateTopicDef } from '../messaging/topics/state';
import { useReactiveVarWithSelector } from 'immerable/useReactiveVarWithSelector';
export const FrameStateTypes = {
  LEADER: 'LEADER',
  FOLLOWER: 'FOLLOWER'
};
const isLeader = stateType => {
  return stateType === FrameStateTypes.LEADER;
};
export const makeFrameState = (stateType, stateName, initialState) => {
  const stateVar = makeImmerVar(initialState);
  const metaVar = makeImmerVar({
    isSyncedWithLeader: isLeader(stateType),
    type: stateType
  });
  const stateSubscribers = [];

  //Helpers

  const subscribeToStateUpdate = subscriber => {
    stateSubscribers.push(subscriber);
    return () => {
      stateSubscribers.splice(stateSubscribers.indexOf(subscriber), 1);
    };
  };
  const updateState = (updater, {
    isStateSync = false
  } = {}) => {
    stateVar(updater);
    stateSubscribers.forEach(subscriber => {
      subscriber({
        isStateSync,
        state: stateVar()
      });
    });
  };
  const syncState = updater => {
    updateState(updater, {
      isStateSync: true
    });
  };
  const isMessageForState = message => {
    return message.payload.stateName === stateName;
  };
  const useSyncState = () => {
    useAsyncEffect(async () => {
      await waitForConnectedMagpie();
      const connection = MagpieConnection.get();
      const topic = connection.useTopic(stateTopicDef);
      // Sync state from external state updates
      const unsubscribeSyncStateMessage = topic.on(StateMessages.syncedStateMessage.getType(), message => {
        if (!isMessageForState(message)) {
          return;
        }
        const {
          state
        } = message.payload;
        syncState(state);
      });
      const unsubscribeStateUpdate = subscribeToStateUpdate(({
        isStateSync,
        state
      }) => {
        // Don't publish a message for updates to state from a sync message otherwise we'll cause a loop
        if (isStateSync) {
          return;
        }
        const message = StateMessages.syncedStateMessage.create({
          state,
          stateName
        });
        connection.useTopic(stateTopicDef).publish(message);
      });
      return () => {
        unsubscribeSyncStateMessage();
        unsubscribeStateUpdate();
      };
    }, []);
  };
  const executeSyncFromLeaderStateCommand = async () => {
    await waitForConnectedMagpie();
    const deferred = getDeferred();
    const connection = MagpieConnection.get();
    const topic = connection.useTopic(stateTopicDef);
    const command = StateCommands.syncFromLeaderStateCommand.create({
      stateName
    });
    topic.publish(command, {
      onReply: reply => {
        deferred.resolve(reply.payload.state);
      },
      onError: error => {
        deferred.reject(error);
      }
    });
    return deferred.promise;
  };

  // PUBLIC

  const setState = updater => {
    updateState(updater);
  };
  const getState = () => {
    return stateVar();
  };
  const useFrameState = () => {
    const frameState = useReactiveVar(stateVar);
    return frameState;
  };
  const useFrameStateWithSelector = selector => {
    const selectedFrameState = useReactiveVarWithSelector(stateVar, selector);
    return selectedFrameState;
  };
  const useMetaState = () => {
    const metaState = useReactiveVar(metaVar);
    return metaState;
  };
  const useSyncFollowerState = () => {
    useSyncState();
    useAsyncEffect(async () => {
      const state = await executeSyncFromLeaderStateCommand();
      syncState(state);
      metaVar(draft => {
        draft.isSyncedWithLeader = true;
      });
    }, []);
  };
  const useSyncLeaderState = () => {
    useSyncState();
    useAsyncEffect(async () => {
      await waitForConnectedMagpie();
      const connection = MagpieConnection.get();
      const topic = connection.useTopic(stateTopicDef);
      const unsubscribeSyncFromLeaderStateCommand = topic.on(StateCommands.syncFromLeaderStateCommand.getType(), (message, {
        reply
      }) => {
        if (!isMessageForState(message)) {
          return;
        }
        const replyMessage = StateMessages.syncedFromLeaderStateMessage.create({
          state: getState(),
          stateName
        });
        reply(replyMessage);
      });
      return () => {
        unsubscribeSyncFromLeaderStateCommand();
      };
    }, []);
  };
  const frameState = {
    setState,
    getState,
    hooks: {
      useState: useFrameState,
      useStateWithSelector: useFrameStateWithSelector,
      useMetaState,
      useSyncState: isLeader(stateType) ? useSyncLeaderState : useSyncFollowerState
    }
  };
  return frameState;
};
export const frameStateBuilder = (config = {}) => {
  const {
    stateType,
    stateName,
    initialState
  } = config;
  return {
    setStateType(newStateType) {
      return frameStateBuilder({
        stateType: newStateType,
        stateName,
        initialState
      });
    },
    setStateName(newStateName) {
      return frameStateBuilder({
        stateType,
        stateName: newStateName,
        initialState
      });
    },
    setInitialState(newInitialState) {
      return frameStateBuilder({
        stateType,
        stateName,
        initialState: newInitialState
      });
    },
    build() {
      if (!stateType || !stateName || !initialState) {
        throw new Error('State type, name, and initial state are required');
      }
      return makeFrameState(stateType, stateName, initialState);
    }
  };
};